import { useNavigate, useSearchParams } from '@/util/react-router-dom';
import { useSsoLogin } from '@/api/sso-login';
import { useCurrentUser } from '@/contexts/current-user-context';
import { useAsync } from '@/hooks/use-async';
import { Loader } from './loaders/loader';
import { useState } from 'react';
import { useCreateSession } from '@/api/session';
import { SsoType } from '@/api/api.schemas';
import { PreprocessRootRedirect } from '@/pages/preprocess-root-redirect';
import { isBrand } from '@/outlets/branding-outlet';

export function RootRedirect() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [blockDefaultNavigate, setBlockDefaultNavigate] = useState(false);

  const sso = searchParams.get('sso') as SsoType;
  const token = searchParams.get('token') ?? searchParams.get('code');
  const seedKey = searchParams.get('seedkey');
  const network = searchParams.get('network');

  const { mutateAsync: createSession } = useCreateSession();
  const { mutate: ssoLogin } = useSsoLogin();
  const { setUserData } = useCurrentUser();

  const { loading } = useAsync(async () => {
    const params: Record<string, string> = {};
    searchParams.forEach((value, key) => {
      params[key] = value;
    });

    // It's okay if we cannot save the session
    const sessionResult = await createSession({ data: params }).catch(() => {});
    if (sessionResult?.redirectUrl) {
      setBlockDefaultNavigate(true);
      const url = sessionResult.redirectUrl;
      if (url.startsWith('http://') || url.startsWith('https://')) {
        window.location.replace(url);
        return;
      }
      await navigate(url, { replace: true, doNotResolveBrandingPath: true });
    }

    if (!sso || !token) return;
    setBlockDefaultNavigate(true);

    ssoLogin(
      { data: { sso, token, seedKey } },
      {
        onSuccess: (result) => {
          setUserData(result.token);
          navigate(`/${result.network}/details-confirmation`, {
            replace: true,
            doNotResolveBrandingPath: true,
          });
        },
        onError: (error) => {
          const networkToUse = error.network ?? network;
          if (!networkToUse || !isBrand(networkToUse)) {
            navigate('/default/sso-error');
            return;
          }
          navigate(`/${networkToUse}/login`, {
            state: { ssoError: true },
          });
        },
      },
    );
  }, [sso, token]);

  if (loading || blockDefaultNavigate) {
    return <Loader type="large" />;
  }

  return <PreprocessRootRedirect to="/default/register" replace doNotResolveBrandingPath />;
}
